<script setup>
import { computed, onMounted, reactive, ref } from "vue";
import SVGIcon from "@/components/SVGIcon.vue";

defineProps({
  mode: { type: String, default: "slide" },
  focusSlides: { type: Boolean, default: false },
});

const shiftWidth = 360;
const gap = 16;

const slides = ref(null);

const state = reactive({
  slides: [],
  index: 0,
  availabilityMap: {},
});

onMounted(() => {
  init();
});

const refresh = () => {
  Object.assign(state, {
    slides: [],
    index: 0,
    availabilityMap: {},
  });

  init();
};

const init = () => {
  const slideArray = Array.from(slides.value.childNodes).filter(
    (node) => node.tagName
  );

  const availableSlides = slideArray.slice(1, slideArray.length);

  availableSlides.map((_slide, index) => {
    const slide = _slide;
    slide.style.left = "0px";

    state.availabilityMap[index] = false;

    if (index === 0) {
      state.availabilityMap[index] = true;
    }

    return slide;
  });

  state.slides = slideArray;
  state.index = 0;
  state.slides[0].classList.add("focused");
};

const handleStackNext = () => {
  state.index += 1;
  const nextSlides = state.slides.slice(state.index, state.slides.length);

  nextSlides.map((_slide) => {
    const shiftAmount = shiftWidth * state.index;
    const slide = _slide;
    slide.style.left = `-${shiftAmount}px`;
    return slide;
  });

  if (typeof state.availabilityMap[state.index - 1] === "boolean") {
    state.availabilityMap[state.index - 1] = false;
  }

  if (typeof state.availabilityMap[state.index + 1] === "boolean") {
    state.availabilityMap[state.index + 1] = true;
  }
};

const handleSlideNext = () => {
  const currFocused = state.slides[state.index];
  const currShift = currFocused.style.left
    ? +currFocused.style.left.replace("px", "")
    : 0;
  const shiftAmount = currShift - currFocused.offsetWidth - gap;

  state.index += 1;

  state.slides.map((_slide) => {
    const slide = _slide;
    slide.style.left = `${shiftAmount}px`;
    slide.classList.remove("focused");
    return slide;
  });
  state.slides[state.index].classList.add("focused");
};

const handleStackPrev = () => {
  state.index -= 1;
  const prevSlides = state.slides.slice(state.index, state.slides.length);

  prevSlides.forEach((_slide) => {
    const shiftAmount = shiftWidth * state.index;
    const slide = _slide;
    slide.style.left = `-${shiftAmount}px`;
    return slide;
  });

  if (typeof state.availabilityMap[state.index + 1] === "boolean") {
    state.availabilityMap[state.index + 1] = false;
  }

  if (typeof state.availabilityMap[state.index - 1] === "boolean") {
    state.availabilityMap[state.index - 1] = true;
  }
};

const handleSlidePrev = () => {
  state.index -= 1;

  const nextFocused = state.slides[state.index];
  const nextShift = nextFocused.style.left
    ? +nextFocused.style.left.replace("px", "")
    : 0;
  const shiftAmount = nextShift + nextFocused.offsetWidth + gap;

  state.slides.map((_slide) => {
    const slide = _slide;
    slide.style.left = `${shiftAmount}px`;
    slide.classList.remove("focused");
    return slide;
  });

  state.slides[state.index].classList.add("focused");
};

const isPrevBtnDisabled = computed(() => {
  return state.index === 0;
});

const isNextBtnDisabled = computed(() => {
  return state.index >= state.slides.length - 1;
});

defineExpose({
  refresh,
});
</script>

<template>
  <div class="fancy-slider">
    <div class="buttons-wrapper">
      <button
        class="slider-btn"
        type="button"
        aria-label="Previous Slide"
        :disabled="isPrevBtnDisabled"
        @click="mode === 'slide' ? handleSlidePrev() : handleStackPrev()"
      >
        <SVGIcon
          class="icon icon-left"
          name="arrow-right"
          :color="'$bluish-v2'"
        />
      </button>
      <button
        class="slider-btn"
        type="button"
        aria-label="Next Slide"
        :disabled="isNextBtnDisabled"
        @click="mode === 'slide' ? handleSlideNext() : handleStackNext()"
      >
        <SVGIcon class="icon" name="arrow-right" :color="'$bluish-v2'" />
      </button>
    </div>
    <div ref="slides" class="slides" :class="{ 'focus-slides': focusSlides }">
      <slot></slot>
    </div>
  </div>
</template>

<style scoped src="./style.scss" lang="scss" />
